import firebase from '../config/firebase';
import apiClient from './api';

const ALLOWED_DOCUMENT_MIMES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

const ALLOWED_DOCUMENT_EXTENSIONS = new Set(['pdf', 'jpg', 'jpeg', 'png', 'webp', 'doc', 'docx']);

export const isPropertyDocumentFile = (file) => {
  if (!file) return false;
  const mime = String(file.type || '').toLowerCase();
  if (ALLOWED_DOCUMENT_MIMES.has(mime)) return true;
  const ext = String(file.name || '').split('.').pop()?.toLowerCase();
  return ALLOWED_DOCUMENT_EXTENSIONS.has(ext);
};

async function uploadPropertyDocumentViaFirebase(propertyId, file) {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Please sign in to upload documents.');
  if (!isPropertyDocumentFile(file)) {
    throw new Error('File type not allowed. Use PDF, Word, or image files.');
  }
  if (file.size > 12 * 1024 * 1024) {
    throw new Error('Document must be 12 MB or smaller.');
  }

  const storage = firebase.storage();
  const safeName = (file.name || 'document.pdf').replace(/[^\w.\-]+/g, '_');
  const path = `property-documents/${propertyId}/${user.uid}/${Date.now()}-${safeName}`;
  const snap = await storage.ref(path).put(file, { contentType: file.type || 'application/octet-stream' });
  const downloadUrl = await snap.ref.getDownloadURL();
  return {
    storagePath: path,
    downloadUrl,
    mimeType: file.type || 'application/octet-stream',
  };
}

export async function uploadPropertyDocument(propertyId, file, title = '') {
  const docTitle = String(title || file?.name || 'Document').trim();

  if (firebase.auth().currentUser) {
    try {
      const uploaded = await uploadPropertyDocumentViaFirebase(propertyId, file);
      const res = await apiClient.registerPropertyDocument(propertyId, {
        title: docTitle,
        ...uploaded,
      });
      return res.data;
    } catch (fbErr) {
      console.warn('[upload] Firebase Storage document:', fbErr.message);
      const msg = (fbErr.message || '').toLowerCase();
      if (msg.includes('unauthorized') || msg.includes('permission')) {
        throw new Error('Document upload denied. Deploy storage rules: firebase deploy --only storage');
      }
      if (!msg.includes('12 mb') && !msg.includes('not allowed')) {
        // Fall through to API upload for storage/bucket issues.
      } else {
        throw fbErr;
      }
    }
  }

  const res = await apiClient.uploadPropertyDocument(propertyId, file, docTitle);
  return res.data;
}
